import { call, select } from "redux-saga/effects";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { AnyEntity, MapEntity } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformMapRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformMapRequest";

import { NO_CONTACT_IN_ENTITY_TYPES } from "@app/scene/dashboard/components/cards/NoContactIn/consts";
import ActivitiesOverdueCardData from "@app/scene/dashboard/store/cards/activitiesOverdue/ActivitiesOverdueCardData";
import NoContactInCardData from "@app/scene/dashboard/store/cards/noContactIn/NoContactInCardData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import ActivitiesOverdueCardConfiguration from "@app/types/dashboard/cards/activitiesOverdue/ActivitiesOverdueCardConfiguration";
import NoContactInCountCardConfiguration from "@app/types/dashboard/cards/noContactInCount/NoContactInCountCardConfiguration";
import FrequencyStatusType from "@app/types/dashboard/FrequencyStatusType";
import Scope from "@app/types/dashboard/Scope";

export function* fetchActivitiesOverdueCardDataHelper(params: {
  callback: (data: ActivitiesOverdueCardData) => void;
  configuration: ActivitiesOverdueCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filters = {
    assigneeId: assigneeIds ? { [PlatformFilterOperator.CONTAINS]: assigneeIds } : undefined,
    completed: false,
    crmActivityTypeId: criteria.activityTypeIds.length
      ? { $in: criteria.activityTypeIds }
      : undefined,
    source: criteria.sources.length ? { $in: criteria.sources } : undefined,
    startAt: { [PlatformFilterOperator.LESS_THAN]: [new Date().toISOString()] },
  };

  const payload = { $filters: filters, $limit: 0 };

  const response: ListResponse<AnyEntity> = yield callApi("fetchActivities", orgId, payload);

  const result: ActivitiesOverdueCardData = { count: response.total };

  yield call(callback, result);
}

export function* fetchNoContactInCountCardDataHelper(params: {
  callback: (data: NoContactInCardData) => void;
  configuration: NoContactInCountCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, extraFilters } = configuration;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const entityTypes =
    Array.isArray(criteria.entityTypes) && criteria.entityTypes.length
      ? criteria.entityTypes
      : NO_CONTACT_IN_ENTITY_TYPES;

  const payload: PlatformMapRequest = {
    $filters: {
      $and: [
        { noContactDaysOut: dateRange.days === null ? null : { $gt: dateRange.days } },
        ...(!scope?.userId && scope?.teamId ? [{ teamId: { $in: [scope.teamId] } }] : []),
        ...(scope?.userId ? [{ userId: { $in: [scope.userId] } }] : []),
        ...(criteria.sources?.length ? [{ source: { $in: criteria.sources } }] : []),
        ...(criteria.frequencyStatus && criteria.frequencyStatus.length === 1
          ? [
              {
                cadenceStatus: {
                  [criteria.frequencyStatus[0] === FrequencyStatusType.UNKNOWN ? "$eq" : "$ne"]:
                    FrequencyStatusType.UNKNOWN,
                },
              },
            ]
          : []),
        ...(extraFilters?.$and ?? []),
        ...(extraFilters?.$or ? [{ $or: extraFilters.$or }] : []),
      ],
      entities: entityTypes.reduce((result, entityType) => ({ ...result, [entityType]: {} }), {}),
    },
    $limit: 0,
  };

  const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, payload);
  yield call(callback, { total: response.total });
}
